import { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { getCatalog } from '../api/client';
import SiteHeader from './shared/SiteHeader';
import { uiLabels } from '../lib/uiLabels';
import { usePageMeta } from '../lib/usePageMeta';

const IMAGE_BASE = (import.meta.env.VITE_IMAGE_URL ?? '').replace(/\/$/, '');

const NIVEL_POR_DIFICULTAD = { FACIL: 1, MEDIO: 2, DIFICIL: 3, 'MUY DIFICIL': 4 };

const PAGE_TITLES = {
  1: 'Ejercicios para Estudiantes',
  4: 'Ejercicios para la Comunidad',
  2: 'Exercises for Students',
};

const TYPE_ORDER = [
  'digitosDirectosInversos',
  'memoriaACortoPalabras',
  'aprendizajeSuperficialProfundo',
  'priming',
  'rotacion',
  'libretest',
];

function imageUrl(path) {
  if (!path) return null;
  const normalized = String(path).replace(/^~\//, '/');
  if (normalized.startsWith('http')) return normalized;
  if (!IMAGE_BASE) return normalized.startsWith('/') ? normalized : `/${normalized}`;
  return `${IMAGE_BASE}${normalized.startsWith('/') ? normalized : `/${normalized}`}`;
}

/** Franja decorativa bajo el header (barra negra). No usar 01.png: es la cabecita. */
function stripImage() {
  return '/assets/img/04.png';
}

export default function ListaTests() {
  const { lenguajeId, categoriaId, tareaId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const testMode = searchParams.get('test') === '1';
  const lid = Number(lenguajeId);
  const isCogni = lid === 4;
  usePageMeta(`${location.state?.tarea?.Descripcion ?? PAGE_TITLES[lid] ?? 'Ejercicios'} | LABPSI`);

  const [resolved, setResolved] = useState(() => ({
    tarea: null,
    categoria: location.state?.categoria,
    lenguaje: location.state?.lenguaje,
  }));
  const [loading, setLoading] = useState(true);
  const [showUp, setShowUp] = useState(false);

  useEffect(() => {
    setLoading(true);
    getCatalog(lid, testMode)
      .then((data) => {
        const cat = data.categorias?.find((c) => c.ID === Number(categoriaId));
        const tarea = cat?.tareas?.find((t) => t.ID === Number(tareaId));
        setResolved({ tarea: tarea ?? null, categoria: cat ?? null, lenguaje: data.lenguaje });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [lid, categoriaId, tareaId, testMode]);

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    window.scrollTo(0, 0);
    setShowUp(false);

    const update = () => {
      const scrollY = window.scrollY || 0;
      const viewH = window.innerHeight;
      const docH = document.documentElement.scrollHeight;
      const canScroll = docH > viewH + 150;
      setShowUp(canScroll && scrollY + viewH >= docH - 80);
    };
    window.addEventListener('scroll', update, { passive: true });
    requestAnimationFrame(() => requestAnimationFrame(update));
    return () => window.removeEventListener('scroll', update);
  }, [lid, categoriaId, tareaId]);

  const { tarea, categoria, lenguaje } = resolved;
  const tests = tarea?.tests ?? {};
  const pageTitle = PAGE_TITLES[lenguaje?.ID] ?? PAGE_TITLES[lid] ?? lenguaje?.Descripcion ?? 'Ejercicios';
  const btnText = (lenguaje?.Textobtn || 'INGRESAR').toUpperCase();
  const themeClass = isCogni ? 'cogni' : 'pedag';
  const labels = uiLabels(lid);

  const goBackToTareas = () =>
    navigate(`/lenguaje/${lenguajeId}/categoria/${categoriaId}${testMode ? '?test=1' : ''}`, {
      state: { categoria, lenguaje },
    });

  const goToCategorias = () =>
    navigate(`/lenguaje/${lenguajeId}${testMode ? '?test=1' : ''}`, { state: { lenguaje } });

  const goDown = () => {
    window.scrollBy({ top: Math.max(300, window.innerHeight * 0.7), behavior: 'smooth' });
  };
  const goUp = () => {
    window.scrollBy({ top: -Math.max(300, window.innerHeight * 0.7), behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-slate-600">Cargando...</p>
      </div>
    );
  }

  if (!tarea) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div>
          <p className="text-slate-600">No hay datos de tarea.</p>
          <button type="button" onClick={goBackToTareas} className="mt-4 text-slate-600 underline bg-transparent border-0 cursor-pointer">
            Volver a tareas
          </button>
        </div>
      </div>
    );
  }

  const allTests = [];
  TYPE_ORDER.forEach((type) => {
    const list = tests[type];
    if (list?.length) list.forEach((test) => allTests.push({ type, test }));
  });

  return (
    <div className={`page-tipo-test page-detalle-test page-lista-tests ${themeClass}`}>
      <div className="navbar-wrapper">
        <SiteHeader lenguajeId={lid} />

        <div className="tipo-test-title-row">
          <button type="button" className="btn-back" onClick={goBackToTareas}>
            {labels.back}
          </button>
          <h1 className="title-pag">
            <button type="button" className="title-page-content title-pag-btn" onClick={goToCategorias}>
              {pageTitle}
            </button>
            <span className="title-pag-sep"> - </span>
            <button type="button" className="title-page-content title-pag-btn" onClick={goBackToTareas}>
              {categoria?.Descripcion}
            </button>
          </h1>
        </div>
      </div>

      <img
        className="detalle-strip"
        src={stripImage()}
        alt=""
      />

      <div className="marketing">
        <div className="sections-container">
          <h2 className="detalle-cat-title">{tarea.Descripcion}</h2>

          {(tarea.marcoteorico || tarea.observaciones) && (
            <p className="detalle-marco">{tarea.marcoteorico || tarea.observaciones}</p>
          )}

          <div className="tipo-test-grid">
            {allTests.map(({ type, test }) => (
              <a
                key={`${type}-${test.ID}`}
                href={`/lenguaje/${lenguajeId}/categoria/${categoriaId}/tarea/${tareaId}/test/${type}/${test.ID}${test.test_mode ? '?test=1' : ''}`}
                className="test-item sub-test-item"
                onClick={(e) => {
                  e.preventDefault();
                  navigate(
                    `/lenguaje/${lenguajeId}/categoria/${categoriaId}/tarea/${tareaId}/test/${type}/${test.ID}${test.test_mode ? '?test=1' : ''}`,
                    { state: { tarea, categoria, lenguaje } }
                  );
                }}
              >
                <div className={`item-content ${isCogni ? 'ej-cogni' : 'ej-pedag'}`}>
                  {test.test_mode && (
                    <span className="lista-tests-badge">TESTING</span>
                  )}
                  <img
                    className="img-circle"
                    src={
                      test.rutaImagen
                        ? imageUrl(test.rutaImagen)
                        : isCogni
                          // Comunidad: ícono de nivel (FACIL/MEDIO/…)
                          ? `/assets/img/nivel_${NIVEL_POR_DIFICULTAD[test.DificultadTexto] ?? 1}.png`
                          // Estudiantes: lápiz y papel (como el LABPSI original; sin barra "FÁCIL")
                          : '/assets/img/imgdef2.png'
                    }
                    alt=""
                    decoding="async"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = isCogni
                        ? '/assets/img/logo.png'
                        : '/assets/img/imgdef2.png';
                    }}
                  />
                  <div className="vertical-center">{test.descripcion}</div>
                  <span className="btn-ingresar">{btnText}</span>
                </div>
              </a>
            ))}
          </div>

          <div className="tipo-test-footer">
            <button
              type="button"
              className="icono-home-new"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              <img src="/assets/img/flecha-volver.png" alt="" />
              <br />{labels.backTopLine1}
              <br />{labels.backTopLine2}
            </button>
          </div>
        </div>
      </div>

      {!showUp && (
        <button type="button" className="icono-abajo" onClick={goDown} aria-label="Desplazar hacia abajo">
          <img src="/assets/img/abajoNew.png" alt="" />
        </button>
      )}
      {showUp && (
        <button type="button" className="icono-arriba" onClick={goUp} aria-label="Desplazar hacia arriba">
          <img src="/assets/img/arribaNew.png" alt="" />
        </button>
      )}
    </div>
  );
}
